import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MessageItem.css';
import { useAuth } from '../../../firebase/AuthProvider';
import { Message } from '../../../services/chatService/ChatService';
import AttachmentDisplay from './AttachmentDisplay';
import MessageRenderer from './MessageRenderer';
import ParticipantBirthdayCard from './ParticipantBirthdayCard';
import { Smile, Pencil, Trash2, Ban, MessageSquare, Bot, Bookmark, File as FileIcon, RefreshCw } from 'lucide-react';
import { ValidateOtpResponseDto } from '../../../interfaces/ExtermalUserDtos/ExternalUsersDtos';
import { MessageType } from '../../../Data/SD';
import { formatSystemMessage } from '../../../utilities/formatSystemMessage';
import Tooltip from '../../ui/Tooltip/Tooltip';
import ConfirmationModal from '../../modal/ConfimationModal/ConfirmationModal';
import { formatChatTimestamp } from '../../../utilities/chat/relativeTimeUtils';
import MilestoneAvatar from '../../ui/Avatar/MilestoneAvatar';
import TicketNotificationCard from './TicketNotificationCard';
import EmojiPicker from '../emojiPicker/EmojiPicker';
import { ChatDeliveryStatus } from '../../../services/chatService/ChatOutbox';

interface MessageItemProps {
    message: Message & {
        isNew?: boolean;
        isOptimistic?: boolean;
        deliveryStatus?: ChatDeliveryStatus;
        optimisticAttachmentName?: string;
    };
    toggledReactionMenuId: number | null;
    onToggleReactionMenu: (messageId: number) => void;
    onAddReaction: (messageId: number, reactionType: string) => void;
    isDateVisible: boolean;
    onToggleDate: (messageId: number) => void;
    externalUserDetails?: ValidateOtpResponseDto | null;
    onEditMessage?: (messageId: number) => void;
    onDeleteMessage?: (messageId: number) => void;
    onOpenThread?: (message: Message) => void;
    onToggleBookmark?: (messageId: number) => void;
    onRetryMessage?: (messageId: number) => void;
    onCancelMessage?: (messageId: number) => void;
    now?: number;
    compact?: boolean;
    appearance?: "default" | "meeting-dark";
    inThread?: boolean;
    groupedWithPrevious?: boolean;
    groupedWithNext?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({
    message,
    toggledReactionMenuId,
    onToggleReactionMenu,
    onAddReaction,
    isDateVisible,
    onToggleDate,
    externalUserDetails,
    onEditMessage,
    onDeleteMessage,
    onOpenThread,
    onToggleBookmark,
    onRetryMessage,
    onCancelMessage,
    now = Date.now(),
    compact = false,
    appearance = "default",
    inThread = false,
    groupedWithPrevious = false,
    groupedWithNext = false,
}) => {
    const isMeetingDark = appearance === "meeting-dark";
    const { currentUser } = useAuth();
    const UserId_search = currentUser ? currentUser.uid : externalUserDetails?.externalUserDetailId;
    const isMine = message.author?.id === UserId_search;
    const isBot = message.isBot === true;
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [toolbarDismissedByPointer, setToolbarDismissedByPointer] = useState(false);
    const reactionButtonRef = useRef<HTMLButtonElement>(null);
    const reactionPopoverRef = useRef<HTMLDivElement>(null);
    const [reactionPopoverPosition, setReactionPopoverPosition] = useState({ top: 0, left: 0 });
    const reactionMenuOpen = toggledReactionMenuId === message.id;

    useLayoutEffect(() => {
        if (!reactionMenuOpen) return;
        const positionPopover = () => {
            const anchor = reactionButtonRef.current?.getBoundingClientRect();
            if (!anchor) return;
            const pickerBounds = reactionPopoverRef.current?.getBoundingClientRect();
            const pickerWidth = pickerBounds?.width || 288;
            const pickerHeight = pickerBounds?.height || 270;
            const gap = 8;
            const desiredTop = anchor.top - pickerHeight - gap >= gap
                ? anchor.top - pickerHeight - gap
                : anchor.bottom + gap;
            const desiredLeft = anchor.left < window.innerWidth / 2
                ? anchor.left
                : anchor.right - pickerWidth;
            setReactionPopoverPosition({
                top: Math.min(Math.max(gap, desiredTop), Math.max(gap, window.innerHeight - pickerHeight - gap)),
                left: Math.min(Math.max(gap, desiredLeft), window.innerWidth - pickerWidth - gap),
            });
        };
        positionPopover();
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(positionPopover)
            : null;
        if (reactionPopoverRef.current) resizeObserver?.observe(reactionPopoverRef.current);
        window.addEventListener('resize', positionPopover);
        window.addEventListener('scroll', positionPopover, true);
        return () => {
            window.removeEventListener('resize', positionPopover);
            window.removeEventListener('scroll', positionPopover, true);
            resizeObserver?.disconnect();
        };
    }, [reactionMenuOpen]);

    useEffect(() => {
        if (!reactionMenuOpen) return;
        const handleOutsideClick = (event: PointerEvent) => {
            const target = event.target as Node;
            if (reactionPopoverRef.current?.contains(target) || reactionButtonRef.current?.contains(target)) return;
            onToggleReactionMenu(message.id);
        };
        document.addEventListener('pointerdown', handleOutsideClick);
        return () => document.removeEventListener('pointerdown', handleOutsideClick);
    }, [message.id, onToggleReactionMenu, reactionMenuOpen]);

    // Access changes are administrative configuration rather than chat history.
    // This suppresses legacy rows created before the backend stopped adding them.
    if (message.systemEventType === "channel.member_access_changed") {
        return null;
    }

    const groupedReactions = (message.reactions || []).reduce((acc, reaction) => {
        if (!acc[reaction.reactionType]) {
            acc[reaction.reactionType] = [];
        }
        // Note: Adjust 'reaction.author?.firstName' based on the actual shape of your Reaction interface
        const displayName = reaction.fullName
            && reaction.userId === UserId_search ? "You" : `${reaction.fullName || ""}`.trim();

        acc[reaction.reactionType].push(displayName);
        return acc;
    }, {} as Record<string, string[]>);
    const hasEngagement = Object.keys(groupedReactions).length > 0 || (message.threadReplyCount ?? 0) > 0;

    const formatMessageTime = (date?: Date | string) => {
        if (message.deliveryStatus === "failed") return "Failed to send";
        if (message.deliveryStatus === "queued") return "Queued";
        if (message.deliveryStatus === "retrying") return "Retrying";
        if (message.deliveryStatus === "sending" || message.isOptimistic) return "Sending";
        return formatChatTimestamp(date, now);
    };

    const compactMessageTime = new Date(message.createDate).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
    });

    const authorLabel = isMine
        ? "You"
        : (message.author?.displayName || `${message.author?.firstName || ""} ${message.author?.lastName || ""}`.trim() || "Unknown sender");

    const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || !["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
        const rows = Array.from(
            event.currentTarget.parentElement?.parentElement?.querySelectorAll<HTMLElement>("[data-chat-message-row]") ?? [],
        );
        const currentIndex = rows.indexOf(event.currentTarget);
        if (currentIndex < 0 || rows.length === 0) return;
        event.preventDefault();
        const targetIndex = event.key === "Home"
            ? 0
            : event.key === "End"
            ? rows.length - 1
            : Math.min(rows.length - 1, Math.max(0, currentIndex + (event.key === "ArrowDown" ? 1 : -1)));
        rows[targetIndex]?.focus();
    };

    if (!message.author || message.messageType === MessageType.SYSTEM || message.messageType === MessageType.CHANNEL_EVENT) {
        return <div className={`my-4 px-4 text-center text-[12.5px] font-medium italic ${isMeetingDark ? 'text-slate-500' : 'text-slate-400'}`}><small>{formatSystemMessage(message.messageContent)}</small></div>;
    }

    const richTextStyles = `max-w-full min-w-0 break-words overflow-hidden text-[13.5px] text-left [&_blockquote]:!border-l-4 [&_blockquote]:!pl-3 [&_blockquote]:!py-1 [&_blockquote]:!my-2 [&_blockquote]:!italic`.replace(/\s+/g, ' ');

    return (
        <div
            className={`message-row-wrapper ${message.isBookmarked ? 'message-row-wrapper--bookmarked' : ''} ${groupedWithPrevious ? 'message-row-wrapper--grouped' : ''} ${groupedWithNext ? 'message-row-wrapper--group-continues' : ''} ${isBot ? 'chat-message-row-bot' : ''} relative w-full border-y border-transparent transition-all ${isBot ? (isMeetingDark ? 'bg-purple-400/[0.035]' : 'bg-purple-50/30') : ''} ${isMeetingDark ? 'hover:border-slate-800 hover:bg-slate-900/70' : 'hover:border-slate-100 hover:bg-slate-50/60'} ${message.deliveryStatus === 'failed' ? 'opacity-90' : ''}`}
            data-chat-message-row
            role="article"
            tabIndex={0}
            aria-label={`${authorLabel}, ${compactMessageTime}${message.isEdited ? ", edited" : ""}${message.isBookmarked ? ", saved" : ""}`}
            onKeyDown={handleRowKeyDown}
            onPointerLeave={() => setToolbarDismissedByPointer(false)}
        >

            <div className={`flex w-full min-w-0 max-w-full ${compact ? `gap-2 px-2.5 ${groupedWithPrevious ? 'py-0.5' : 'pb-1 pt-1.5'}` : `gap-2.5 px-4 sm:px-5 ${groupedWithPrevious ? 'py-0.5' : 'pb-1.5 pt-2'}`} ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>

                <div className="message-avatar-gutter group/avatar relative mt-0.5 flex h-8 w-8 shrink-0 items-start justify-center">
                    {groupedWithPrevious ? (
                        <span className="message-group-hover-time pointer-events-none mt-1 hidden whitespace-nowrap text-[9px] font-medium text-slate-400 group-hover/avatar:block" aria-hidden="true">
                            {compactMessageTime}
                        </span>
                    ) : isBot ? (
                        <div
                            className={`chat-bot-avatar flex h-8 w-8 items-center justify-center rounded-full border ${isMeetingDark ? 'border-purple-400/30 bg-purple-400/15 text-purple-200' : 'border-purple-200 bg-purple-50 text-[#6F49B5] dark:border-purple-400/20 dark:bg-purple-400/15 dark:text-purple-200'}`}
                            aria-label="Waivera bot"
                        >
                            <Bot size={17} strokeWidth={2.25} />
                        </div>
                    ) : (
                        <MilestoneAvatar
                            size="xxs"
                            userId={message.author.id}
                            displayName={`${message.author.firstName || ""} ${message.author.lastName || ""}`.trim()}
                            alternate
                        />
                    )}
                </div>

                <div className={`flex flex-col min-w-0 flex-1 relative ${isMine ? 'items-end' : 'items-start'}`}>

                    {groupedWithPrevious && (
                        <span className="sr-only">
                            {authorLabel}, {compactMessageTime}
                        </span>
                    )}

                    {!groupedWithPrevious && <div className={`flex items-baseline gap-2 px-1 ${compact ? "mb-0.5" : "mb-1"} ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                        <span className={`chat-message-author ${isBot ? 'chat-message-author--bot' : ''} text-[12.5px] font-semibold ${isBot ? (isMeetingDark ? 'text-purple-100' : 'text-[#5F3D9E] dark:text-purple-200') : isMeetingDark ? 'text-slate-100' : 'text-slate-900 dark:text-slate-100'}`}>
                            {authorLabel}
                        </span>
                        {isBot && (
                            <span className={`chat-message-automated-badge rounded-full border px-2 py-0.5 text-[8.5px] font-bold tracking-[0.1em] ${isMeetingDark ? 'border-purple-400/25 bg-purple-400/15 text-purple-200' : 'border-purple-200 bg-purple-50 text-[#6F49B5]'}`}>
                                AUTOMATED
                            </span>
                        )}
                        <div className="flex items-center gap-1.5">
                            <span role={message.isOptimistic || message.deliveryStatus === 'failed' ? "status" : undefined} aria-live="polite" className={`chat-message-time flex items-center gap-1.5 text-[11px] font-medium ${message.deliveryStatus === 'failed' ? 'text-red-500 dark:text-red-300' : message.isOptimistic ? 'text-slate-400 dark:text-slate-500' : isMeetingDark ? 'text-slate-300' : 'text-slate-500'}`}>
                                {message.isOptimistic && message.deliveryStatus !== 'failed' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#7E57C2]" aria-hidden="true" />}
                                {formatMessageTime(message.createDate)}
                            </span>
                            {message.isEdited && !message.isDeleted && !message.isOptimistic && (
                                <span className={`text-[10px] font-medium italic ${isMeetingDark ? 'text-slate-400' : 'text-slate-400'}`}>(edited)</span>
                            )}
                        </div>
                    </div>}

                    {message.isDeleted ? (
                        <div role="status" className={`chat-deleted-message flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] italic select-none ${isMeetingDark ? 'bg-slate-900 text-slate-400' : 'bg-slate-100 text-slate-500 dark:bg-[#1d202b] dark:text-slate-400'}`}>
                            <Ban size={14} className="shrink-0" />
                            <span>This message was deleted</span>
                        </div>
                    ) : (
                        <div className={`chat-message-bubble ${isMine ? 'chat-message-bubble-mine' : 'chat-message-bubble-other'} ${isBot ? 'chat-message-bubble-bot' : ''} relative mb-0.5 w-fit min-w-0 max-w-full overflow-hidden text-left transition-all ${richTextStyles} ${isBot ? 'rounded-xl px-3 py-2.5' : compact ? "max-w-[90%] rounded-xl px-3 py-1.5" : "max-w-[720px] rounded-xl px-3.5 py-2.5"} ${isBot ? (isMeetingDark ? 'bg-slate-900/65 !text-slate-100 shadow-[0_1px_2px_rgba(0,0,0,0.16)] [&_blockquote]:!border-purple-300' : 'bg-slate-50 !text-slate-800 shadow-[0_1px_3px_rgba(15,23,42,0.05)] dark:bg-slate-800 dark:!text-slate-100 [&_blockquote]:!border-purple-300') : isMine ? (compact ? 'bg-purple-50 !text-slate-800 dark:bg-purple-400/15 dark:!text-slate-100 [&_a]:!text-[#6F49B5] dark:[&_a]:!text-purple-300 [&_blockquote]:!border-slate-300' : isMeetingDark ? 'bg-purple-400/10 text-slate-100 [&_blockquote]:!border-purple-400' : 'bg-indigo-50/60 text-slate-900 dark:bg-purple-400/10 dark:text-slate-100 [&_blockquote]:!border-indigo-400') : (compact ? (isMeetingDark ? 'bg-slate-800 !text-slate-100 [&_blockquote]:!border-slate-600' : 'bg-slate-100 !text-slate-800 dark:bg-slate-800 dark:!text-slate-100 [&_blockquote]:!border-slate-300') : isMeetingDark ? 'bg-slate-900/65 text-slate-100 [&_blockquote]:!border-slate-600' : 'bg-white text-slate-800 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:bg-slate-800 dark:text-slate-100 dark:shadow-none [&_blockquote]:!border-slate-300')}`}>
                            {/* Ticket events have structured data and get a purpose-built card. */}
                            {isBot && message.systemEventType === "participant.birthday_digest" ? <ParticipantBirthdayCard data={message.systemEventData} /> : isBot && message.systemEventType === "ticket.update" ? (
                                <TicketNotificationCard
                                    data={message.systemEventData}
                                    appearance={appearance}
                                    fallback={<MessageRenderer message={message} appearance={appearance} automated />}
                                />
                            ) : (
                                <MessageRenderer message={message} appearance={appearance} automated={isBot} />
                            )}

                            {/* Attachments use the shared renderer so image clicks can open a global preview. */}
                            {message.attachmentUrl && (message.isOptimistic ? (
                                message.attachmentType === 'image' ? (
                                    <img
                                        src={message.attachmentUrl}
                                        alt={message.optimisticAttachmentName || "Pending attachment"}
                                        className="mt-2 max-h-64 max-w-full rounded-xl object-contain"
                                    />
                                ) : (
                                    <div className="mt-2 flex max-w-sm items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-700 dark:bg-slate-700 dark:text-slate-100">
                                        <FileIcon size={17} className="shrink-0" />
                                        <span className="min-w-0 truncate text-xs font-semibold">{message.optimisticAttachmentName || "Attachment"}</span>
                                    </div>
                                )
                            ) : (
                                <AttachmentDisplay
                                    message={message}
                                    attachmentUrl={message.attachmentUrl}
                                    attachmentType={message.attachmentType || ''}
                                />
                            ))}

                            {message.deliveryStatus === "failed" && (onRetryMessage || onCancelMessage) && (
                                <div className="mt-2 flex items-center gap-1.5">
                                    {onRetryMessage && (
                                        <button
                                            type="button"
                                            onClick={(event) => { event.stopPropagation(); onRetryMessage(message.id); }}
                                            className="flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                                        >
                                            <RefreshCw size={13} />
                                            Retry
                                        </button>
                                    )}
                                    {onCancelMessage && (
                                        <button
                                            type="button"
                                            onClick={(event) => { event.stopPropagation(); onCancelMessage(message.id); }}
                                            className="rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            )}

                        </div>
                    )}

                    {!message.isDeleted && hasEngagement && !message.isOptimistic && (
                        <div className={`chat-message-engagement mt-1 flex max-w-full flex-wrap items-center gap-1.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                            {Object.entries(groupedReactions).map(([emoji, userNames]) => {
                                const tooltipText = `${userNames.join(', ')} reacted with ${emoji}`;
                                const hasMyReaction = (message.reactions || []).some((reaction) => reaction.reactionType === emoji && reaction.userId === UserId_search);

                                return (
                                    <Tooltip key={emoji} content={<span className="text-xs font-medium">{tooltipText}</span>} position="bottom">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAddReaction(message.id, emoji);
                                            }}
                                            className={`chat-reaction-chip flex min-h-7 items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-semibold transition ${hasMyReaction
                                                ? 'chat-reaction-chip-active'
                                                : ''
                                            } ${hasMyReaction
                                                ? isMeetingDark ? 'bg-purple-400/20 text-purple-100' : 'bg-indigo-100 text-indigo-700 dark:bg-purple-400/20 dark:text-purple-100'
                                                : isMeetingDark ? 'bg-slate-800 text-slate-100 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-indigo-50 dark:bg-slate-700 dark:text-slate-100'
                                            }`}
                                        >
                                            <span className="text-base leading-none">{emoji}</span>
                                            <span>{userNames.length}</span>
                                        </button>
                                    </Tooltip>
                                );
                            })}

                            {!inThread && (message.threadReplyCount ?? 0) > 0 && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onOpenThread?.(message); }}
                                    className={`chat-thread-chip flex min-h-7 items-center gap-1.5 rounded-lg px-2.5 py-0.5 text-[11px] font-semibold transition ${isMeetingDark ? 'bg-purple-400/15 text-purple-200 hover:bg-purple-400/25' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-purple-400/15 dark:text-purple-200'}`}
                                >
                                    <MessageSquare size={14} />
                                    <span>{message.threadReplyCount} {message.threadReplyCount === 1 ? 'reply' : 'replies'}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {!message.isDeleted && !message.isOptimistic && (
                <div className={`message-action-toolbar ${reactionMenuOpen ? 'message-action-toolbar--pinned' : ''} ${toolbarDismissedByPointer ? 'message-action-toolbar--dismissed' : ''} absolute ${compact ? "top-1" : "-top-3"} ${isMine ? 'left-3' : 'right-3'} z-10 flex items-center rounded-lg border-[1px] p-0.5 shadow-md ${isMeetingDark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                    <div className="relative">
                        <Tooltip content={<span className="text-xs font-medium">React</span>} position="top">
                            <button data-emoji-picker-trigger type="button" aria-label="React to message" aria-expanded={reactionMenuOpen} ref={reactionButtonRef} onClick={(e) => { e.stopPropagation(); onToggleReactionMenu(message.id); }} className={`p-1.5 rounded-md transition-colors flex items-center justify-center outline-none ${isMeetingDark ? 'text-slate-400 hover:text-purple-300 hover:bg-slate-700' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                <Smile size={16} strokeWidth={2.5} />
                            </button>
                        </Tooltip>

                        {reactionMenuOpen && createPortal(
                            <div
                                ref={reactionPopoverRef}
                                className="chat-reaction-popover fixed z-[99999]"
                                style={reactionPopoverPosition}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <EmojiPicker
                                    compact
                                    autoFocusSearch
                                    onDismiss={() => onToggleReactionMenu(message.id)}
                                    onSelect={(emoji) => {
                                        onAddReaction(message.id, emoji);
                                    }}
                                />
                            </div>,
                            document.body,
                        )}
                    </div>

                    <ConfirmationModal
                        isOpen={isDeleteDialogOpen}
                        onClose={() => setIsDeleteDialogOpen(false)}
                        onConfirm={() => onDeleteMessage?.(message.id)}
                        title="Delete Message"
                        text="Are you sure you want to delete this message? This action cannot be undone and it will be removed for everyone in the conversation."
                        confirmButtonText="Delete"
                        cancelButtonText="Cancel"
                    />

                    {!inThread && (
                        <>
                            <div className={`w-px h-4 mx-0.5 ${isMeetingDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                            <Tooltip content={<span className="text-xs font-medium">Reply in Thread</span>} position="top">
                                <button type="button" aria-label="Reply in thread" onClick={(e) => { e.stopPropagation(); onOpenThread?.(message); }} className={`p-1.5 rounded-md transition-colors flex items-center justify-center outline-none ${isMeetingDark ? 'text-slate-400 hover:text-purple-300 hover:bg-slate-700' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                    <MessageSquare size={15} strokeWidth={2.5} />
                                </button>
                            </Tooltip>
                        </>
                    )}

                    {onToggleBookmark && (
                        <>
                            <div className={`w-px h-4 mx-0.5 ${isMeetingDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                            <Tooltip content={<span className="text-xs font-medium">{message.isBookmarked ? "Remove from saved" : "Save message"}</span>} position="top">
                                <button
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (event.detail > 0) setToolbarDismissedByPointer(true);
                                        onToggleBookmark(message.id);
                                    }}
                                    aria-label={message.isBookmarked ? "Remove saved message" : "Save message"}
                                    className={`p-1.5 rounded-md transition-colors flex items-center justify-center outline-none ${message.isBookmarked ? 'bg-purple-50 text-[#7E57C2]' : isMeetingDark ? 'text-slate-400 hover:text-purple-300 hover:bg-slate-700' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                >
                                    <Bookmark size={15} strokeWidth={2.5} fill={message.isBookmarked ? "currentColor" : "none"} />
                                </button>
                            </Tooltip>
                        </>
                    )}

                    {isMine && (onEditMessage || onDeleteMessage) && (
                        <>
                            <div className={`w-px h-4 mx-0.5 ${isMeetingDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                            {onEditMessage && (
                                <Tooltip content={<span className="text-xs font-medium">Edit</span>} position="top">
                                    <button type="button" aria-label="Edit message" onClick={(e) => { e.stopPropagation(); onEditMessage(message.id); }} className={`p-1.5 rounded-md transition-colors flex items-center justify-center outline-none ${isMeetingDark ? 'text-slate-400 hover:text-purple-300 hover:bg-slate-700' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                        <Pencil size={15} strokeWidth={2.5} />
                                    </button>
                                </Tooltip>
                            )}
                            {onDeleteMessage && (
                                <Tooltip content={<span className="text-xs font-medium">Delete</span>} position="top">
                                    <button type="button" aria-label="Delete message" onClick={(e) => { e.stopPropagation(); setIsDeleteDialogOpen(true); }} className={`p-1.5 rounded-md transition-colors flex items-center justify-center outline-none ${isMeetingDark ? 'text-slate-400 hover:text-red-300 hover:bg-red-500/10' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}>
                                        <Trash2 size={15} strokeWidth={2.5} />
                                    </button>
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MessageItem;
